import { useLocalObservable } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { Call } from '@gem/call/CallStore/Call';
import { Opponent } from '@gem/call/CallStore/Opponent/Opponent';
import { useCallContext } from '@gem/call/CallView/CallView.context';

interface IShareScreenToggle {
  ongoingCall: Call;
  screenSharingOpponent: Opponent | null;
  activeOpponentUid: ProviderUid | null;
  isGridLayoutView: boolean;
  toggleGridLayout(): void;
}

export const useShareScreenToggle = (): IShareScreenToggle => {
  const context = useCallContext();

  const presenter = useLocalObservable<IShareScreenToggle>(() => ({
    get ongoingCall(): Call {
      return context.call;
    },

    get screenSharingOpponent(): Opponent | null {
      return presenter.ongoingCall.opponents.screenSharingOpponent;
    },

    get activeOpponentUid(): ProviderUid | null {
      return presenter.ongoingCall.activeOpponentUid;
    },

    get isGridLayoutView(): boolean {
      return presenter.ongoingCall.viewState.isGridLayout;
    },

    toggleGridLayout(): void {
      context.callController.toggleGridLayout(null);
    },
  }));

  const prevScreenSharingOpponentUidRef = useRef<ProviderUid | null>();
  const prevOpponentUidRef = useRef<ProviderUid | null>();

  useEffect(() => {
    if (presenter.screenSharingOpponent) {
      if (presenter.isGridLayoutView) {
        presenter.toggleGridLayout();
      }
      context.callController.setActiveOpponent(presenter.screenSharingOpponent);
    } else if (
      prevScreenSharingOpponentUidRef.current === prevOpponentUidRef.current &&
      !presenter.isGridLayoutView
    ) {
      presenter.toggleGridLayout();
    }
  }, [presenter.screenSharingOpponent]);

  useEffect(() => {
    prevOpponentUidRef.current = presenter.activeOpponentUid;
    prevScreenSharingOpponentUidRef.current = presenter.screenSharingOpponent?.uid;
  }, [presenter.screenSharingOpponent, presenter.activeOpponentUid]);

  return presenter;
};
